/**
 * Font utilities for After Effects compatible fonts
 * Loads fonts from server to ensure consistency with backend rendering
 * Note: PostScript Type 1 fonts are not supported by After Effects (deprecated Jan 2023)
 * After Effects uses specific naming like "SegoeUI-Bold" for font variants
 */

import api from '../services/api';

/**
 * Get system fonts from server
 * Fetches fonts available on the server for After Effects rendering
 * @returns {Promise<Array>} Array of system font objects from server
 */
export const getSystemFonts = async () => {
  try {
    const response = await api.get('/fonts/system');
    
    if (response.data.success && response.data.fonts) {
      console.log(`Loaded ${response.data.count} fonts from server (${response.data.serverOS})`);
      return response.data.fonts;
    } else {
      throw new Error(response.data.error || 'Failed to load fonts');
    }
  } catch (error) {
    console.error('Error fetching fonts from server:', error);
    
    // Fallback to basic fonts if server request fails
    return getFallbackFonts();
  }
};

/**
 * Get fallback fonts if server request fails
 * @returns {Array} Array of basic fallback fonts
 */
const getFallbackFonts = () => {
  const basicFonts = [
    'Arial', 'Arial-Bold', 'Arial-Italic',
    'TimesNewRoman', 'TimesNewRoman-Bold', 
    'Helvetica', 'Helvetica-Bold',
    'CourierNew', 'CourierNew-Bold',
    'Verdana', 'Georgia', 'Tahoma'
  ];
  
  return basicFonts.map(font => ({
    name: font.replace(/-/g, ' ').replace(/([A-Z])/g, ' $1').trim(),
    fontFamily: font.split('-')[0].replace(/([A-Z])/g, ' $1').trim(),
    aeFontName: font,
    category: 'system'
  }));
};

/**
 * Normalize font name for After Effects compatibility
 * After Effects expects the exact font family name as registered in the system
 * @param {string} fontName - Font name to normalize
 * @returns {string} Normalized font family name
 */
export const normalizeFontName = (fontName) => {
  if (!fontName) return '';
  
  // After Effects expects exact font family names
  // e.g., "Arial", "Arial Black", "Helvetica Neue"
  return fontName.trim();
};

/**
 * Format font name for display
 * @param {string} fontName - Font family name
 * @returns {string} Human-readable display name
 */
export const formatDisplayName = (fontName) => {
  if (!fontName) return '';
  
  // Most font names are already human-readable
  return fontName.trim();
};

/**
 * Create a font option object from any font value
 * This ensures we can always display the current font, even if not in our list
 * @param {string} fontValue - After Effects font name (e.g., "SegoeUI-Bold")
 * @returns {Object} Font option object
 */
export const createFontOption = (fontValue) => {
  // Parse AE font format (e.g., "SegoeUI-Bold" -> "Segoe UI Bold")
  let displayName = fontValue;
  let fontFamily = fontValue;
  
  // Check if it's in AE format with dash
  if (fontValue.includes('-')) {
    const parts = fontValue.split('-');
    // Add spaces to camelCase: SegoeUI -> Segoe UI
    const baseName = parts[0].replace(/([A-Z])/g, ' $1').trim();
    const variant = parts[1];
    displayName = `${baseName} ${variant}`;
    fontFamily = baseName;
  } else {
    // Add spaces to camelCase if needed
    displayName = fontValue.replace(/([A-Z])/g, ' $1').trim();
    fontFamily = displayName;
  }
  
  return {
    name: displayName,
    fontFamily: fontFamily,
    aeFontName: fontValue, // Keep original AE format
    category: 'custom',
    variants: []
  };
};

/**
 * Search/filter fonts by name
 * @param {Array} fonts - Array of font objects
 * @param {string} searchTerm - Search term
 * @returns {Array} Filtered fonts
 */
export const filterFonts = (fonts, searchTerm) => {
  if (!searchTerm) return fonts;
  
  const term = searchTerm.toLowerCase();
  return fonts.filter(font => 
    font.name.toLowerCase().includes(term) ||
    (font.fontFamily && font.fontFamily.toLowerCase().includes(term)) ||
    (font.aeFontName && font.aeFontName.toLowerCase().includes(term))
  );
};

/**
 * Category display names
 */
export const FONT_CATEGORIES = {
  system: 'Server Fonts',
  custom: 'Current Font'
};